import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { toast } from "sonner";
import { Eraser, ShieldCheck } from "@phosphor-icons/react";

const SignaturePad = ({ onSave, busy }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [signerName, setSignerName] = useState("");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = "#1A2F24";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };
  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => setDrawing(false);
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };
  const save = () => {
    if (!signerName.trim()) {
      toast.error("Please type your printed name first.");
      return;
    }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave(dataUrl, signerName);
  };

  return (
    <div className="space-y-3">
      <p className="label-eyebrow">Draw your signature below</p>
      <canvas
        ref={canvasRef}
        className="signature-pad"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="public-signature-canvas"
      />
      <div className="grid md:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <div>
          <label className="label-eyebrow block mb-2">Your printed name</label>
          <input
            data-testid="public-signer-name"
            className="input-clean"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
          />
        </div>
        <button onClick={clear} className="btn-outline" data-testid="public-signature-clear">
          <Eraser size={14} className="inline mr-2" />
          Clear
        </button>
        <button
          onClick={save}
          disabled={busy}
          className="btn-primary"
          data-testid="public-signature-save"
        >
          {busy ? "Sealing…" : "Sign & Accept"}
        </button>
      </div>
    </div>
  );
};

const PublicSignPage = () => {
  const { token } = useParams();
  const [letter, setLetter] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await publicApi.get(`/public/letter/${token}`);
      setLetter(r.data);
    } catch {
      setErr("This signing link is invalid or has expired.");
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const sign = async (signature_data_url, signer_name) => {
    setBusy(true);
    try {
      await publicApi.post(`/public/letter/${token}/sign`, { signature_data_url, signer_name });
      toast.success("Engagement letter signed. Thank you.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record signature.");
    } finally {
      setBusy(false);
    }
  };

  if (err)
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="card-flat max-w-md text-center">
          <p className="label-eyebrow">Error</p>
          <h1 className="font-serif text-3xl text-[#112018] mt-2">Link Not Valid</h1>
          <p className="text-sm text-[#606C64] mt-3">{err}</p>
        </div>
      </div>
    );

  if (!letter)
    return (
      <div className="min-h-screen flex items-center justify-center label-eyebrow">
        Loading…
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F7F6F2] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Firm header */}
        <div className="flex items-center gap-3 mb-12">
          <div className="w-10 h-10 bg-[#1A2F24] flex items-center justify-center">
            <span className="text-[#F7F6F2] font-serif text-xl">L</span>
          </div>
          <div>
            <p className="font-serif text-xl text-[#112018] leading-none">{letter.firm_name}</p>
            <p className="label-eyebrow leading-none mt-1">Engagement Letter</p>
          </div>
        </div>

        {/* Letter */}
        <div className="card-flat" data-testid="public-letter">
          <p className="label-eyebrow">{letter.service_type}</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#112018] mt-2 leading-tight">
            For <em className="text-[#A8977B] not-italic">{letter.client_name}</em>
          </h1>

          <div className="mt-8 space-y-4 text-[15px] leading-relaxed text-[#1A2F24]">
            <p>Dear {letter.client_name},</p>
            <p>
              Thank you for choosing <strong>{letter.firm_name}</strong>. This letter confirms our
              engagement to provide <em>{letter.service_type}</em> services. Our fixed professional
              fee for this engagement is{" "}
              <span className="font-mono">${letter.fee.toLocaleString()}</span>.
            </p>
            {letter.description && (
              <div className="border-l-2 border-[#A8977B] pl-4 italic text-[#1A2F24]">
                {letter.description}
              </div>
            )}
            <p>
              Our services are governed by the standards of the AICPA. We will maintain
              confidentiality of all information you provide and will use it only for the purposes
              of this engagement.
            </p>
            <p>
              By signing below, you accept the scope, professional fee, and terms outlined above.
              This signature is electronically captured and legally binding.
            </p>
          </div>

          <div className="mt-10 pt-8 border-t border-[#1A2F24]/12">
            {letter.signed ? (
              <div data-testid="public-letter-signed">
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck size={20} className="text-[#1A2F24]" weight="fill" />
                  <p className="label-eyebrow text-[#1A2F24]">Signed & Sealed</p>
                </div>
                {letter.signature_data_url && (
                  <img
                    src={letter.signature_data_url}
                    alt="signature"
                    className="border border-[#1A2F24]/20 bg-white max-h-32 mb-3"
                  />
                )}
                <p className="text-sm text-[#1A2F24]">
                  Signed by <strong>{letter.signer_name}</strong>
                </p>
                <p className="font-mono text-xs text-[#606C64] mt-1">
                  {new Date(letter.signed_at).toLocaleString()}
                </p>
                <p className="text-xs text-[#606C64] mt-4 italic">
                  A copy has been recorded and is available to {letter.firm_name}.
                </p>
              </div>
            ) : (
              <SignaturePad onSave={sign} busy={busy} />
            )}
          </div>
        </div>

        <p className="text-center text-xs text-[#606C64] mt-8 label-eyebrow">
          Secured signing link · {letter.firm_name}
        </p>
      </div>
    </div>
  );
};

export default PublicSignPage;
